import React, { useState, useEffect, useRef, ChangeEvent } from 'react'
import { useInvoiceAPI } from './api'
import { Invoice, Pagination, InvoiceResponses, InvoiceStatus } from './model'
import PaginationUI from 'react-js-pagination'
import moment from 'moment'
import { formatMoney } from './utils'
import { ButtonGroup } from 'reactstrap'
import { Button, Form, FormGroup, Label, Input, Card, CardBody, CardTitle, CardText } from 'reactstrap'

import Autosuggest from 'react-autosuggest'

import * as userStore from 'stores/user'
type InvoiceableType = 'restaurant' | 'service_provider' | 'rds'

import { toV6Query as searchRestaurants } from 'api/restaurant-search'
import Icon from 'react-fa'
import { Cell, Grid, Row } from 'views/order/v5/fragments/comp/grid'
import useDebounce from 'utils/use-debounce'

const statuses = [
  { id: 'completed', label: 'Ready for payment' },
  { id: 'payment_postponed', label: 'Payment postponed' },
  { id: 'running', label: 'Generating' },
  { id: 'failed', label: 'Failed' },
  { id: 'paid', label: 'Paid/Cleared' },
  { id: 'skipped', label: 'Ignored' },
  { id: 'ach_ready', label: 'ACH Ready for payment' },
  { id: 'ach_pending', label: 'ACH Waiting to complete' },
  { id: 'ach_failed', label: 'ACH Transfer failed' },
  { id: 'ach_paid', label: 'ACH Payment completed' },
]

export default function Invoices(): JSX.Element {
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [pagination, setPagination] = useState<Pagination | null>(null)
  const invoiceAPI = useInvoiceAPI()
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | null>('completed')
  const [invoiceableFilter, setInvoiceableFilter] = useState<InvoiceableFilter | null>(null)

  const StatusFilter = () => (
    <div className="form-group">
      <label htmlFor="status-filter">Filter by status:</label>
      <select
        id="status-filter"
        value={statusFilter || ''}
        onChange={(e) => {
          setPage(1)
          setStatusFilter(e.target.value != '' ? (e.target.value as InvoiceStatus) : null)
        }}>
        {statuses.map(({ id, label }) => (
          <option value={id} key={id}>
            {label}
          </option>
        ))}
        <option value="">All</option>
      </select>
    </div>
  )

  useEffect(() => {
    invoiceAPI
      .getAllInvoices(
        statusFilter,
        invoiceableFilter ? invoiceableFilter.type : null,
        invoiceableFilter ? invoiceableFilter.id || null : null,
        invoiceableFilter ? invoiceableFilter.name || null : null,
        page,
        20
      )
      .then((r: InvoiceResponses) => {
        setInvoices(r.invoices)
        setPagination(r.pagination)
      })
  }, [page, statusFilter, invoiceableFilter])

  return (
    <div className="row-fluid">
      <h4>Statements</h4>
      {!pagination && <div>Loading statements</div>}
      {pagination && (
        <>
          <StatusFilter />
          {userStore.hasAnyRole(
            'billing',
            'billing_admin',
            'support',
            'ambassador',
            'restaurant_admin',
            'rds_admin'
          ) && <InvoiceableFilter filter={invoiceableFilter} setFilter={setInvoiceableFilter} />}
          {userStore.hasAnyRole('billing', 'billing_admin', 'support', 'ambassador') && (
            <BulkManageActions filters={invoiceableFilter} status={statusFilter} />
          )}
        </>
      )}
      <table className="table table-striped table-hover">
        <thead>
          <tr>
            <th className="text-left">Statement for</th>
            <th className="text-right">Gross Amount</th>
            <th className="text-right">Amount Received</th>
            <th className="text-right">Balance Due</th>
            <th className="text-right">Billings Count</th>
            <th className="text-center">Status</th>
            <th className="text-center">Statement date</th>
          </tr>
        </thead>
        <tbody>
          {invoices.map((i: Invoice) => (
            <InvoiceLine key={i.id} {...i} />
          ))}
        </tbody>
      </table>
      {pagination && <div>You have {pagination.total} invoices</div>}
      {pagination && (
        <PaginationUI
          prevPageText="prev"
          nextPageText="next"
          firstPageText="first"
          lastPageText="last"
          itemsCountPerPage={20}
          activePage={page}
          pageRangeDisplayed={3}
          onChange={setPage}
          totalItemsCount={pagination.total}
          itemClass="page-item"
          linkClass="page-link"
        />
      )}
    </div>
  )
}

interface InvoiceableFilter {
  id: number | null
  name: string | null
  type: InvoiceableType
}

interface Suggestion {
  id: number | string
  name: string
}

function InvoiceableFilter(props: {
  filter: Partial<InvoiceableFilter> | null
  setFilter: (filter: Partial<InvoiceableFilter> | null) => void
}): JSX.Element {
  const [type, setType] = useState<InvoiceableType | null>(null)
  const [searchTerm, setSearchTerm] = useState<string | null>(
    props.filter && props.filter.name ? props.filter.name : null
  )
  const [selection, setSelection] = useState<Suggestion | null>(null)
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])

  const clear = (): void => {
    setType(null)
    setSearchTerm(null)
    setSelection(null)
    setSuggestions([])
    props.setFilter(null)
  }

  const search = (): void => {
    if (type == 'restaurant' && selection != null) props.setFilter({ id: parseInt(selection.id as string), type: type })
    if (!!type && type != 'restaurant' && searchTerm) {
      props.setFilter({ type, name: searchTerm })
    }
  }

  const inputProps = {
    type: 'text',
    placeholder: 'search...',
    value: searchTerm || '',
    onChange: (_event: ChangeEvent<HTMLInputElement>, { newValue }: { newValue: string }) => {
      setSearchTerm(newValue)
    },
  }

  const [searchRestaurantTerm, setSearchRestaurantTerm] = useState('')
  const searchRestaurantDebouncedTerm = useDebounce(searchRestaurantTerm, 500)
  const [loadingData, setLoadingData] = useState(false)

  useEffect(() => {
    setLoadingData(true)
    searchRestaurants(searchRestaurantDebouncedTerm)
      .firstToPromise()
      .then((res: { data: Suggestion[] }) => {
        setLoadingData(false)
        setSuggestions(res.data)
      })
      .catch(() => setLoadingData(false))
  }, [searchRestaurantDebouncedTerm])

  const fetchSuggestions = ({ value }: { value: string }): void => {
    switch (type) {
      case 'restaurant':
        setSearchRestaurantTerm(value)
        setSuggestions([])
        break
    }
  }
  const clearSuggestions = (): void => {
    setSuggestions([])
  }
  const selectSuggestion = (
    _event: ChangeEvent<HTMLInputElement>,
    { suggestion }: { suggestion: Suggestion }
  ): void => {
    setSelection(suggestion)
  }

  return (
    <div className="form-group">
      <label htmlFor="invoiceable-filter">Search by: </label>
      <select
        id="type-filter"
        value={type || ''}
        onChange={(e) => {
          if (e.target.value == 'restaurant' || e.target.value == 'service_provider' || e.target.value == 'rds') {
            setType(e.target.value)
          } else {
            setType(null)
          }
          setSearchTerm(null)
        }}>
        <option value="">---</option>
        <option value="restaurant">restaurant</option>
        <option value="service_provider">service provider</option>
        <option value="rds">delivery service provider</option>
      </select>{' '}
      {type == 'restaurant' && (
        <div style={{ display: 'inline-block' }}>
          <Autosuggest
            suggestions={suggestions}
            onSuggestionsFetchRequested={fetchSuggestions}
            onSuggestionsClearRequested={clearSuggestions}
            onSuggestionSelected={selectSuggestion}
            getSuggestionValue={(s: Suggestion) => s.name}
            renderSuggestion={(s: Suggestion) => <>{s.name}</>}
            inputProps={inputProps}
          />
        </div>
      )}
      {type == 'restaurant' && loadingData && (
        <i
          className="fa fa-spinner"
          style={{ position: 'absolute', zIndex: 1000, marginLeft: '-2.5ex', marginTop: '1.5ex' }}></i>
      )}
      {type != 'restaurant' && (
        <input
          type="text"
          name="service_provider"
          value={searchTerm || ''}
          placeholder="enter provider name..."
          onChange={(ev) => setSearchTerm(ev.target.value)}
        />
      )}
      {((type == 'restaurant' && selection != null) ||
        (type != 'restaurant' && searchTerm && searchTerm.length > 1)) && (
        <Button variant="primary" onClick={search}>
          search
        </Button>
      )}
      {type != null && (
        <Button variant="light" onClick={clear}>
          clear
        </Button>
      )}
    </div>
  )
}

function InvoiceLine(props: Invoice): JSX.Element {
  const invoiceAPI = useInvoiceAPI()
  const [busy, setBusy] = useState(false)

  const onDownloadInvoiceOrders = () => {
    if (!busy) {
      setBusy(true)
      invoiceAPI
        .downloadInvoiceOrders(props.id)
        .then((_: void) => {
          alert(`We are processing your request. The results will be sent to you by email at ${userStore.email()}`)
          setBusy(false)
        })
        .catch((_: void) => {
          setBusy(false)
          alert('Could not process the request. Please retry again later.')
        })
    }
  }

  return (
    <tr className={invoiceTypeToRowClass(props.status)}>
      <td>
        <button
          className={`btn btn-sm ${busy ? 'btn-danger' : 'btn-default'}`}
          onClick={onDownloadInvoiceOrders}
          title="Download invoice orders">
          <Icon name="download" />
        </button>{' '}
        {['completed', 'paid', 'payment_postponed', 'ach_paid', 'ach_pending', 'ach_ready', 'ach_failed'].includes(
          props.status
        ) ? (
          <a href={`#/statements/${props.invoiceable_type}/${props.id}`}>{props.invoiceable_name || 'Unknown'}</a>
        ) : (
          <>{props.invoiceable_name || 'Unknown'}</>
        )}
        <span className="text text-muted">{props.invoiceable_type}</span>{' '}
        <span className="text text-muted">({props.invoiceable_id})</span>
      </td>
      <td className="text-right">{formatMoney(props.revenue / 100, 'en')}</td>
      <td className="text-right">{props.revenue_received ? formatMoney(props.revenue_received / 100, 'en') : ''}</td>
      <td className="text-right">{formatMoney(props.total / 100, 'en')}</td>
      <td className="text-right">{props.billables_count || 0}</td>
      <td className="text-center" title={props.failed_reason}>
        {props.status}
      </td>
      <td className="text-center">{moment(props.inserted_at).format('lll')}</td>
    </tr>
  )
}

function invoiceTypeToRowClass(status: string): string {
  switch (status) {
    case 'failed':
      return 'danger'
    case 'running':
      return 'warning'
    case 'payment_postponed':
      return 'warning'
    case 'completed':
      return 'info'
    case 'cleared':
      return 'success'
    case 'skipped':
      return ''
    default:
      return 'warning'
  }
}

function BulkManageActions(props: { filters: InvoiceableFilter; status: string | null }): JSX.Element {
  const invoiceAPI = useInvoiceAPI()
  const [running, setRunning] = useState(false)
  const [uploadFormVisible, showUploadForm] = useState(false)
  const [downloadFormVisible, setDownloadFormVisible] = useState(false)
  const [uploadReady, setUploadReady] = useState(false)
  const uploadForm = useRef<HTMLFormElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  enum DownloadType {
    All = 'all',
    TimeLimited = 'limited',
  }
  const [downloadType, setDownloadType] = useState<DownloadType>(DownloadType.All)
  const [startDate, setStartDate] = useState<string | null>(
    moment()
      .subtract(7, 'day')
      .format('YYYY-MM-DD')
  )
  const [endDate, setEndDate] = useState<string | null>(
    moment()
      .add(1, 'day')
      .format('YYYY-MM-DD')
  )

  const downloadInvoices = (downloadType: DownloadType, startDate: string | null, endDate: string | null): void => {
    setDownloadFormVisible(false)
    setRunning(true)
    const res =
      downloadType == DownloadType.All
        ? invoiceAPI.downloadAll(props.status as string, null, null)
        : invoiceAPI.downloadAll(props.status as string, startDate, endDate)
    res
      .then(() => {
        alert('All set! You will get all the invoices by email in couple of minutes!')
      })
      .catch((err) => {
        console.error('Download error', err)
        alert('Download failed')
      })
      .finally(() => setRunning(false))
  }

  const onSubmit = (): void => {
    if (uploadForm.current != null && fileRef.current != null) {
      const formData = new FormData()
      formData.append('invoice', fileRef.current.files[0])
      setRunning(true)

      invoiceAPI
        .uploadAll(formData)
        .then(() => alert('File uploaded success. Please wait until we mark all invoices as paid'))
        .catch((err) => {
          console.error('Upload error', err)
          alert('Failed to upload data')
        })
        .finally(() => setRunning(false))
    }
  }

  const renderUploadForm = (): JSX.Element => (
    <div
      style={{
        height: '190px',
        backgroundColor: 'white',
        border: '3px solid #ccc',
        borderRadius: '10px',
        filter: 'drop-shardow(2px 2px 2px #aaa)',
      }}>
      <Form encType="multipart/form-data" method="post" id="upload-form" innerRef={uploadForm}>
        <Card>
          <CardBody>
            <CardTitle>Upload Statements</CardTitle>
            <FormGroup>
              <Label for="fileInput">Upload all paid statements file.</Label>
              <Input
                type="file"
                innerRef={fileRef}
                name="invoice[file]"
                accept="text/csv"
                onChange={() => setUploadReady(!!fileRef.current && fileRef.current.files.length === 1)}
              />
            </FormGroup>
            <ButtonGroup>
              <Button outline size="sm" color="primary" onClick={onSubmit} disabled={!uploadReady || running}>
                Upload!
              </Button>
              <Button outline size="sm" color="danger" onClick={() => showUploadForm(false)}>
                Cancel
              </Button>
            </ButtonGroup>
          </CardBody>
        </Card>
      </Form>
    </div>
  )

  const renderDownloadForm = () => {
    const statusLabel = props.status != '' ? statuses.find(({ id }) => id == props.status)?.label : 'All'
    return (
      <div
        style={{
          height: '270px',
          backgroundColor: 'white',
          border: '3px solid #ccc',
          borderRadius: '10px',
          filter: 'drop-shardow(2px 2px 2px #aaa)',
        }}>
        <Form method="post" id="upload-form" innerRef={uploadForm} className="form-inline">
          <Card>
            <CardBody>
              <CardTitle>Download {statusLabel?.toLowerCase()} statements </CardTitle>
              <Grid>
                <Row>
                  <Cell span={2}>
                    <FormGroup className="checkbox">
                      <Label className="checkbox">
                        <Input
                          type="radio"
                          name="download_type"
                          value="all"
                          checked={downloadType == DownloadType.All}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            setDownloadType(e.target.value as DownloadType)
                          }
                          className="checkbox"
                        />{' '}
                        All statements available within the list below
                      </Label>
                    </FormGroup>
                    <FormGroup className="checkbox">
                      <Label className="checkbox">
                        <Input
                          type="radio"
                          name="download_type"
                          value="limited"
                          checked={downloadType == DownloadType.TimeLimited}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            setDownloadType(e.target.value as DownloadType)
                          }
                          className="checkbox"
                        />{' '}
                        Time Limited
                      </Label>
                    </FormGroup>
                  </Cell>
                </Row>
                {downloadType == DownloadType.TimeLimited && (
                  <Row>
                    <Cell>
                      <FormGroup>
                        <Label for="fileInput">Start Date</Label>
                        <Input
                          type="date"
                          name="start_date"
                          value={startDate}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => setStartDate(e.target.value)}
                        />
                      </FormGroup>
                    </Cell>
                    <Cell>
                      <FormGroup>
                        <Label for="fileInput">End Date</Label>
                        <Input
                          type="date"
                          name="end_date"
                          value={endDate}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => setEndDate(e.target.value)}
                        />
                      </FormGroup>
                    </Cell>
                  </Row>
                )}
              </Grid>
              <ButtonGroup>
                <Button
                  outline
                  size="sm"
                  color="primary"
                  onClick={() => downloadInvoices(downloadType, startDate, endDate)}>
                  Create Download Request
                </Button>
                <Button outline size="sm" color="danger" onClick={() => setDownloadFormVisible(false)}>
                  Cancel
                </Button>
              </ButtonGroup>
            </CardBody>
          </Card>
        </Form>
      </div>
    )
  }

  return (
    <div className="form-group">
      {userStore.hasAnyRole('billing_admin', 'restaurant_admin', 'ambassador') &&
        props.status != '' &&
        props.status != null && (
          <Button onClick={() => setDownloadFormVisible(true)} disabled={running}>
            Download All Statements
          </Button>
        )}
      {userStore.hasAnyRole('billing_admin') && !uploadFormVisible && (
        <Button onClick={() => showUploadForm(true)}>Upload Paid Statements</Button>
      )}
      {uploadFormVisible && renderUploadForm()}
      {downloadFormVisible && renderDownloadForm()}
    </div>
  )
}
